/* eslint-disable max-len */
import * as DG from 'datagrok-api/dg';
import wu from 'wu';
import { Vector } from '@datagrok-libraries/utils/src/type-declarations';
import { vectorDotProduct, vectorLength } from '@datagrok-libraries/utils/src/vector-operations';
import { CandidateSimType } from './types';
import { ALPHABET, Alphabets, candidateAlphabets, GAP_SYMBOL, GapOriginals, monomerRe, NOTATION } from './consts';
import { AminoacidsPalettes } from '../../aminoacids';
import { NucleotidesPalettes } from '../../nucleotides';
import { UnknownSeqPalettes } from '../../unknown';
import { cleanupHelmSymbol } from '../../helm/utils';
import { HelmTypes, PolymerTypes } from '../../helm/consts';
export class StringListSeqSplitted {
    get length() { return this.mList.length; }
    get graphInfo() {
        return undefined;
    }
    ;
    isGap(posIdx) {
        return this.getOriginal(posIdx) === this.gapOriginalMonomer;
    }
    /** @param {number} posIdx monomer position 0-based index */
    getCanonical(posIdx) {
        if (this.length <= posIdx)
            throw new Error('Index out of bounds');
        const om = this.mList[posIdx];
        return om !== this.gapOriginalMonomer ? om : GAP_SYMBOL;
    }
    getOriginal(posIdx) {
        if (this.length <= posIdx)
            throw new Error('Index out of bounds');
        return this.mList[posIdx];
    }
    getCanonicalRegion(start, end) {
        const actStart = Math.min(Math.max(0, start), this.length);
        const actEnd = Math.max(Math.min(this.length, end), 0);
        const len = actEnd - actStart;
        const gap = this.gapOriginalMonomer;
        return new Array(len).fill(null).map((_, i) => { const om = this.mList[i + actStart]; return om === gap ? GAP_SYMBOL : om; });
    }
    getOriginalRegion(start, end) {
        const actStart = Math.min(Math.max(0, start), this.length);
        const actEnd = Math.max(Math.min(this.length, end), 0);
        const len = actEnd - actStart;
        return new Array(len).fill(null).map((_, i) => this.mList[i + actStart]);
    }
    get gapOriginal() {
        return this.gapOriginalMonomer;
    }
    constructor(mList, gapOriginalMonomer) {
        this.mList = mList;
        this.gapOriginalMonomer = gapOriginalMonomer;
    }
}
/** Same as string list splitted, but for helm will also encode the graph info, and define a getter for it
 *
 */
export class HelmSplitted extends StringListSeqSplitted {
    constructor(mListSeparated, // list of lists of monomers (separated by | in helm notation
    connections, // string part of helm defining connections between monomers
    polymerTypes, // polymer types for each disjoint sequence part
    gapOriginalMonomer) {
        super(mListSeparated.flat(), gapOriginalMonomer);
        this.mListSeparated = mListSeparated;
        this.connections = connections;
        this._graphInfo = undefined;
        this.polymerTypes = mListSeparated.map((n, i) => new Array(n.length).fill(polymerTypes[i])).flat();
    }
    get graphInfo() {
        this._graphInfo ?? (this._graphInfo = this.parseConnections());
        return this._graphInfo;
    }
    parseConnections() {
        const graphInfo = {
            connections: [],
            disjointSeqStarts: [],
            polymerTypes: this.polymerTypes
        };
        // Start positions of each disjoint sequence part (chain). This is
        // independent of connections: a HELM like RNA1{...}|RNA2{...}$$$$ has two
        // disjoint chains even though it declares no inter-chain connections.
        // Always record the chain boundaries so downstream consumers (multi-chain
        // atomic-level assembly, the '|' chain separator in the cell renderer)
        // can see them.
        let seqStart = 0;
        for (let i = 0; i < this.mListSeparated.length; ++i) {
            graphInfo.disjointSeqStarts.push(seqStart);
            seqStart += this.mListSeparated[i].length;
        }
        if ((this.connections?.length ?? 0) > 0) {
            // parse connections like PEPTIDE2,PEPTIDE2,16:R2-1:R1
            const connectionParts = (this.connections ?? '').split('|').filter((cp) => (cp?.length ?? 0) > 0);
            const sequenceConSeqIdxRe = /^(PEPTIDE|RNA|BLOB|CHEM)\d{1,2}$/;
            const sequenceConRe = /^\d{1,2}:R\d{1}-\d{1,2}:R\d{1}$/;
            for (const cp of connectionParts) {
                const cpParts = cp.split(',');
                if (cpParts.length !== 3 || !sequenceConRe.test(cpParts[2]) || !sequenceConSeqIdxRe.test(cpParts[0]) || !sequenceConSeqIdxRe.test(cpParts[1]))
                    continue;
                const seq1 = parseInt(cpParts[0].replace(/^(PEPTIDE|RNA|BLOB|CHEM)/, '')) - 1;
                const seq2 = parseInt(cpParts[1].replace(/^(PEPTIDE|RNA|BLOB|CHEM)/, '')) - 1;
                const seq1Type = cpParts[0].replace(/\d{1,2}$/, '');
                const seq2Type = cpParts[1].replace(/\d{1,2}$/, '');
                if (seq1 < 0 || seq1 >= this.mListSeparated.length || seq2 < 0 || seq2 >= this.mListSeparated.length)
                    continue;
                const conParts = cpParts[2].split('-');
                if (conParts.length !== 2)
                    continue;
                const con1Parts = conParts[0].split(':');
                const con2Parts = conParts[1].split(':');
                if (con1Parts.length !== 2 || con2Parts.length !== 2)
                    continue;
                const con1 = parseInt(con1Parts[0]) - 1;
                const con2 = parseInt(con2Parts[0]) - 1;
                if (con1 < 0 || con1 >= this.mListSeparated[seq1].length || con2 < 0 || con2 >= this.mListSeparated[seq2].length)
                    continue;
                graphInfo.connections.push({
                    seq1Type: seq1Type,
                    seq2Type: seq2Type,
                    seqIndex1: seq1,
                    seqIndex2: seq2,
                    monomerIndex1: con1,
                    monomerIndex2: con2,
                    rGroup1: parseInt(con1Parts[1].substring(1)),
                    rGroup2: parseInt(con2Parts[1].substring(1)),
                });
            }
        }
        return graphInfo;
    }
}
/** Gets canonical monomers for original ones with cyclization marks */
export class BilnSeqSplitted extends HelmSplitted {
    getCanonical(posIdx) {
        if (this.isGap(posIdx))
            return GAP_SYMBOL;
        const om = this.getOriginal(posIdx);
        let cmRes = om;
        const re = /\(\d{1,2},\d{1,2}\)$/; // there can be multiple cyclization marks
        while (re.test(cmRes))
            cmRes = cmRes.replace(re, '');
        return cmRes;
    }
    getOriginal(posIdx) {
        const om = super.getOriginal(posIdx);
        // replace [] with '' for biln
        return om.replace(/^\[(.*)\]/, '$1');
    }
    constructor(seqListSplit, gapOriginalMonomer) {
        super(seqListSplit, '', seqListSplit.map(() => PolymerTypes.PEPTIDE), gapOriginalMonomer);
    }
    parseConnections() {
        // create graph info for cyclization marks
        // EG: A(1,2).G(1,1)(2,2).C(2,1) -> first number is the bond identifier, second is the R group (1-based)
        // . is used as separator between different sequences
        const graphInfo = { connections: [], disjointSeqStarts: [] };
        const connectionStorage = {};
        let seqLengthCounter = 0;
        for (let i = 0; i < this.mListSeparated.length; i++) {
            const ss = this.mListSeparated[i];
            graphInfo.disjointSeqStarts.push(seqLengthCounter);
            seqLengthCounter += ss.length;
            for (let j = 0; j < ss.length; j++) {
                const om = ss[j];
                const re = /\(\d{1,2},\d{1,2}\)$/; // there can be multiple cyclization marks
                let cmRes = om;
                while (re.test(cmRes)) {
                    const con = /\(\d{1,2},\d{1,2}\)$/.exec(cmRes)?.[0];
                    cmRes = cmRes.replace(re, '');
                    if (con != null) {
                        const nums = con.substring(1, con.length - 1).split(',').map((s) => parseInt(s, 10));
                        if (nums.length !== 2 || nums.some((n) => n == null || Number.isNaN(n)))
                            continue; // this should never happen, but just in case
                        const bondId = nums[0].toString();
                        const rGroup = nums[1];
                        if (bondId in connectionStorage) {
                            // there is already a monomer with this bondId
                            const prev = connectionStorage[bondId];
                            graphInfo.connections.push({
                                seq1Type: PolymerTypes.PEPTIDE, // for biln, its always peptides
                                seq2Type: PolymerTypes.PEPTIDE,
                                seqIndex1: prev.seqIndex,
                                seqIndex2: i,
                                monomerIndex1: prev.monomerIndex,
                                monomerIndex2: j,
                                rGroup1: prev.rGroup,
                                rGroup2: rGroup
                            });
                            delete connectionStorage[bondId];
                        }
                        else
                            connectionStorage[bondId] = { seqIndex: i, monomerIndex: j, rGroup: rGroup }; // store for later
                    }
                }
            }
        }
        return graphInfo;
    }
}
export class FastaSimpleSeqSplitted {
    get length() { return this.seqS.length; }
    isGap(posIdx) {
        return this.getOriginal(posIdx) === GapOriginals[NOTATION.FASTA];
    }
    getCanonical(posIdx) {
        if (this.length <= posIdx)
            throw new Error('Index out of bounds');
        if (this.isGap(posIdx))
            return GAP_SYMBOL;
        return this.seqS[posIdx];
    }
    getOriginal(posIdx) {
        if (this.length <= posIdx)
            throw new Error('Index out of bounds');
        return this.seqS[posIdx];
    }
    getCanonicalRegion(start, end) {
        const actStart = Math.max(0, start);
        const actEnd = Math.min(this.length, end);
        const len = actEnd - actStart;
        const gap = GapOriginals[NOTATION.FASTA];
        return new Array(len).fill(null).map((_, i) => {
            const om = this.seqS[i + actStart];
            return om === gap ? GAP_SYMBOL : om;
        });
    }
    getOriginalRegion(start, end) {
        const actStart = Math.max(0, start);
        const actEnd = Math.min(this.length, end);
        return this.seqS.slice(actStart, actEnd); // hacky way but quite effective
    }
    get gapOriginal() {
        return GapOriginals[NOTATION.FASTA];
    }
    constructor(seqS) {
        this.seqS = seqS;
    }
}
/** Stats of sequences with specified splitter func, returns { freq, sameLength }.
 * @param {DG.Column} seqCol
 * @param {number} minLength
 * @param {SplitterFunc} splitter
 * @return { SeqColStats }, sameLength: boolean } stats of column sequences
 */
export function getStatsForCol(seqCol, minLength, splitter) {
    const cats = seqCol.categories;
    const splitted = wu.enumerate(seqCol.getRawData())
        .map(([catI, rowIdx]) => splitter(cats[catI]));
    return getStats(splitted, minLength);
}
function getStats(splitted, minLength) {
    const freq = {};
    let sameLength = true;
    let firstLength = null;
    for (const seqSS of splitted) {
        if (firstLength == null)
            firstLength = seqSS.length;
        else if (seqSS.length !== firstLength)
            sameLength = false;
        if (seqSS.length >= minLength) {
            for (let posIdx = 0; posIdx < seqSS.length; ++posIdx) {
                const cm = seqSS.getCanonical(posIdx);
                if (!(cm in freq))
                    freq[cm] = 0;
                freq[cm] += 1;
            }
        }
    }
    return { freq: freq, sameLength: sameLength };
}
/** Split sequence for single character monomers, square brackets multichar monomer names or gap symbol.
 * @param seq object with sequence
 * @param getMonomer Source of the {@link seq} string
 * @return {string[]} array of monomers
 */
export const splitterAsFasta = (seq) => {
    const mmList = wu(seq.toString().matchAll(monomerRe))
        .map((ma) => {
        return ma[2] ?? ma[1]; // preserve '-' as gap symbol for compatibility with simpleAsFastaSimple
    }).toArray();
    return new StringListSeqSplitted(mmList, GapOriginals[NOTATION.FASTA]);
    // return new Proxy(splittedList as object, {
    //   get(target: string[], p: string | symbol, receiver: any): any {
    //     const k = 11;
    //   }
    // }) as ISeqSplitted;
};
export const splitterAsFastaSimple = (seq) => {
    return !!seq ? new FastaSimpleSeqSplitted(seq) : new StringListSeqSplitted([], GapOriginals[NOTATION.FASTA]);
};
/** Gets method to split sequence by separator
 * @param separator Monomer separator
 * @param limit
 * @return {SplitterFunc}
 */
export function getSplitterWithSeparator(separator, limit = undefined) {
    return (seq) => {
        if (!seq)
            return new StringListSeqSplitted([], GapOriginals[NOTATION.SEPARATOR]);
        else {
            let mmList;
            const mRe = new RegExp(`(?<=^|\\${separator})("-"|'-'|[^\\${separator}]*)(?=\\${separator}|$)`, 'g'); // depends on separator args
            if (limit != undefined) {
                mRe.lastIndex = 0;
                mmList = wu(seq.matchAll(mRe)).take(limit).map((ea) => ea[0]).toArray();
            }
            else
                mmList = seq.replaceAll('\"-\"', '').replaceAll('\'-\'', '').split(separator, limit);
            return new StringListSeqSplitted(mmList, GapOriginals[NOTATION.SEPARATOR]);
        }
    };
}
// this scary thing is a pattern for matching HELM RNA notation with most complex cases like [Sug(smt)]([NNs])[pm()].
export const RNA_HELM_TRIPLET_MONOMER_REG = /^((?:[^\[\]()]|\[[^\[\]]*\])+)\(((?:[^\[\]()]|\[[^\[\]]*\])+)\)((?:[^\[\]()]|\[[^\[\]]*\])+)$/;
// this one matches the terminal phosphate-less monomer in RNA HELM notation, e.g. [dR](A) or [R](A)
export const RNA_HELM_TERMINAL_PHOSPHATELESS_MONOMER_REG = /^((?:[^\[\]()]|\[[^\[\]]*\])+)\(((?:[^\[\]()]|\[[^\[\]]*\])+)\)$/;
/** Splits Helm string to monomers, but does not replace monomer names to other notation (e.g. for RNA).
 * Only for linear polymers, does not split RNA for ribose and phosphate monomers.
 * EG byciclic helm: PEPTIDE1{F}|PEPTIDE2{[dI].[Trp_Ome].[Asp_OMe].[Cys_Bn].[meG].[Phe_3Cl].[dD].T.[dI].T.[dK].[aG].[3Pal].[xiIle].[meD].[Ala_tBu]}|PEPTIDE3{L.[Pro_4Me3OH].S.[NMe2Abz].Q.[3Pal].[xiIle].[D-Hyp].[Ala_tBu].[dI].[Trp_Ome].[Asp_OMe].N.[meG].[Phe_34diCl].[Phe_34diCl]}$PEPTIDE2,PEPTIDE2,16:R2-1:R1|PEPTIDE3,PEPTIDE3,16:R2-1:R1|PEPTIDE3,PEPTIDE2,10:R3-1:R3|PEPTIDE1,PEPTIDE2,1:R2-9:R3$$$V2.0
 * Also should support smiles in monomer names, like: PEPTIDE1{[ac].L.N.[*N[C@H](C(=O)*)Cc1ccc(cc1)OP(=O)(O)O |$_R1;;;;;_R2;;;;;;;;;;;;$|].I.D.L.D.L.V.[am]}$$$$
 * @param {string} seq Source string of HELM notation
 * @param {ISeqSource} src Source of the {@link seq} string
 * @return {string[]}
 */
export const splitterAsHelm = (seq) => {
    const indexOfSequenceEnd = seq.indexOf('}$');
    const sequencePart = seq.substring(0, indexOfSequenceEnd + 1);
    const connectionsEndPart = seq.indexOf('$', indexOfSequenceEnd + 2);
    const connectionsPart = seq.substring(indexOfSequenceEnd + 2, connectionsEndPart);
    // const helmParts = seq.split('$');
    const spList = sequencePart.split('}|');
    // since we removed }|, need to add } to last part
    for (let i = 0; i < spList.length - 1; i++)
        spList[i] = spList[i] + '}';
    const polymerTypes = spList.map((sp) => sp.replace(/\d{1,2}\{.+\}/, ''));
    const mListSplit = spList
        .map((sp, i) => (sp.match(/(?<=\{).+(?=})/)?.[0]?.split('.') ?? [])
        .flatMap((m) => {
        if (polymerTypes[i] === PolymerTypes.RNA) {
            const match = RNA_HELM_TRIPLET_MONOMER_REG.exec(m);
            if (match != null)
                return [cleanupHelmSymbol(match[1]), cleanupHelmSymbol(match[2]), cleanupHelmSymbol(match[3])];
            const terminalMatch = RNA_HELM_TERMINAL_PHOSPHATELESS_MONOMER_REG.exec(m);
            if (terminalMatch != null)
                return [cleanupHelmSymbol(terminalMatch[1]), cleanupHelmSymbol(terminalMatch[2])];
        }
        return [cleanupHelmSymbol(m)];
    }));
    const res = new HelmSplitted(mListSplit, connectionsPart ?? '', polymerTypes, GapOriginals[NOTATION.HELM]);
    return res;
};
export function splitterAsBiln(seq) {
    const disjointPartsRaw = seq.split('.')
        .map((ds) => ds.replaceAll('\"-\"', '').replaceAll('\'-\'', ''));
    // some monomers might contain '-', and need to be enclosed in [], e.g. A-[D-Arg]-C
    // replace all separators inside [] with some temporary string, then split by separator, then restore
    const tempSep = '___TEMP___';
    for (let i = 0; i < disjointPartsRaw.length; i++) {
        const re = /\[([^\]]+)\]/g;
        let match;
        let dp = disjointPartsRaw[i];
        while ((match = re.exec(disjointPartsRaw[i])) !== null) {
            const inside = match[1];
            const replaced = inside.replaceAll('-', tempSep);
            dp = dp.replace(inside, replaced);
        }
        disjointPartsRaw[i] = dp;
    }
    const disjointParts = disjointPartsRaw
        .map((ds) => ds.split('-')
        .map((m) => m.replaceAll(tempSep, '-')));
    //const totalLength = disjointParts.reduce((acc, ss) => acc + ss.length, 0);
    const res = new BilnSeqSplitted(disjointParts, GapOriginals[NOTATION.SEPARATOR]);
    return res;
}
/** Get splitter method to split sequences to monomers (required for MacromoleculeDifferenceCellRenderer)
 * @param {string} units
 * @param {string} separator
 * @param limit
 * @return {SplitterFunc}
 */
export function getSplitter(units, separator, limit = undefined) {
    if (units.toLowerCase().startsWith(NOTATION.FASTA))
        return splitterAsFasta;
    else if (units.toLowerCase().startsWith(NOTATION.SEPARATOR))
        return getSplitterWithSeparator(separator, limit);
    else if (units.toLowerCase().startsWith(NOTATION.HELM))
        return splitterAsHelm;
    else if (units.toLowerCase().startsWith(NOTATION.BILN))
        return splitterAsBiln;
    else
        throw new Error(`Unexpected units ${units} .`);
    // TODO: Splitter for HELM
}
const longMonomerPartRe = /([^\W_]+)/g;
/** Convert long monomer names to short ones */
export function monomerToShort(amino, maxLengthOfMonomer) {
    if (amino.length <= maxLengthOfMonomer)
        return amino;
    //const kebabAmino = amino.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '-' : '') + $);
    const shortAminoMatch = amino.match(longMonomerPartRe);
    const needAddDots = amino.length > maxLengthOfMonomer || (shortAminoMatch?.length ?? 0) > 1;
    const shortAmino = shortAminoMatch?.[0] ?? ' ';
    return !needAddDots ? shortAmino : shortAmino.substring(0, maxLengthOfMonomer - 1) + '…';
}
/** */
export function getAlphabet(alphabet) {
    switch (alphabet) {
        case ALPHABET.DNA:
            return Alphabets.fasta.dna;
        case ALPHABET.RNA:
            return Alphabets.fasta.rna;
        case ALPHABET.PT:
            return Alphabets.fasta.peptide;
        default:
            throw new Error(`Unsupported alphabet '${alphabet}'.`);
    }
}
/** Calculate similarity in current sequence and alphabet.
 * @param {MonomerFreqs} freq
 * @param {Set<string>} alphabet
 * @param {string} gapSymbol
 * @return {number} Cosine similarity
 */
export function getAlphabetSimilarity(freq, alphabet, gapSymbol = '-') {
    const keys = new Set([...new Set(Object.keys(freq)), ...alphabet]);
    keys.delete(gapSymbol);
    const freqA = [];
    const alphabetA = [];
    for (const m of keys) {
        freqA.push(m in freq ? freq[m] : 0);
        alphabetA.push(alphabet.has(m) ? 1 : 0);
    }
    /* There were a few ideas: chi-squared, pearson correlation (variance?), scalar product */
    const freqV = new Vector(freqA);
    const alphabetV = new Vector(alphabetA);
    return vectorDotProduct(freqV, alphabetV) / (vectorLength(freqV) * vectorLength(alphabetV));
}
/** From detectMacromolecule */
export function detectAlphabet(freq, candidates, gapSymbol = '-') {
    const candidatesSims = candidates.map((c) => {
        const sim = getAlphabetSimilarity(freq, c.alphabet, gapSymbol);
        return new CandidateSimType(c, freq, sim);
    });
    let alphabetName;
    const maxSim = Math.max(...candidatesSims.map((cs) => cs.similarity > cs.cutoff ? cs.similarity : -1));
    if (maxSim > 0) {
        const sim = candidatesSims.find((cs) => cs.similarity === maxSim);
        alphabetName = sim.name;
    }
    else
        alphabetName = ALPHABET.UN;
    return alphabetName;
}
export function detectHelmAlphabet(freq, candidates, gapSymbol = '-') {
    // helm can contain DNA/RNA and other monomers. need to check that
    const mons = Object.keys(freq);
    const helmNucleotideFullRe = /\(|\)/;
    // heuristic for detecting dna/rna
    const hasBranching = mons.filter((m) => m.split(helmNucleotideFullRe).filter((p) => !!p).length === 3).length > mons.length * 0.8;
    const correctedFreqs = hasBranching ? Object.entries(freq)
        .reduce((acc, [m, f]) => {
        const split = m.split(helmNucleotideFullRe);
        const actualMonomer = split[1]; // the inside part is the actual monomer (for example r(A)p)
        if (actualMonomer)
            acc[actualMonomer] = f;
        return acc;
    }, {}) : freq;
    return detectAlphabet(correctedFreqs, candidates, gapSymbol);
}
/** Selects a suitable palette based on column data
 * @param {DG.Column} seqCol Column to look for a palette
 * @param {number}  minLength minimum length of sequence to detect palette (empty strings are allowed)
 * @return {SeqPalette} Palette corresponding to the alphabet of the sequences in the column
 */
export function pickUpPalette(seqCol, seqHelper, minLength = 5) {
    let alphabet;
    if (seqCol.semType == DG.SEMTYPE.MACROMOLECULE) {
        const sh = seqHelper.getSeqHandler(seqCol);
        alphabet = sh.alphabet;
    }
    else {
        const stats = getStatsForCol(seqCol, minLength, splitterAsFasta);
        alphabet = detectAlphabet(stats.freq, candidateAlphabets);
    }
    const res = getPaletteByType(alphabet);
    return res;
}
export function getPaletteByType(paletteType) {
    switch (paletteType) {
        case ALPHABET.PT:
            return AminoacidsPalettes.GrokGroups;
        case ALPHABET.DNA:
        case ALPHABET.RNA:
            return NucleotidesPalettes.Chromatogram;
        // other
        default:
            return UnknownSeqPalettes.Color;
    }
}
export function pickUpSeqCol(df) {
    const semTypeColList = df.columns.bySemTypeAll(DG.SEMTYPE.MACROMOLECULE);
    let resCol = semTypeColList.find((col) => {
        const units = col.meta.units;
        return units ? units.indexOf('MSA') !== -1 : false;
    }) ?? null;
    if (!resCol && semTypeColList.length > 0)
        resCol = semTypeColList[0];
    return resCol;
}
export function polymerTypeToHelmType(pt) {
    switch (pt) {
        case PolymerTypes.PEPTIDE:
            return HelmTypes.AA;
        case PolymerTypes.RNA:
            return HelmTypes.NUCLEOTIDE;
        case PolymerTypes.CHEM:
            return HelmTypes.CHEM;
        case PolymerTypes.BLOB:
            return HelmTypes.BLOB;
        default:
            return HelmTypes.AA;
    }
}
//# sourceMappingURL=utils.js.map